import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import {
  CloudIcon,
  CheckCircleIcon,
  XCircleIcon,
  ArrowPathIcon,
  Cog6ToothIcon,
  UserGroupIcon,
  ShoppingCartIcon,
  TicketIcon,
  CubeIcon,
  PhoneIcon,
  ChatBubbleLeftRightIcon
} from '@heroicons/react/24/outline';
import { crmAPI } from '../services/api';

const CRMConfigPage = () => {
  const [config, setConfig] = useState(null);
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('salesforce');
  const [syncStatus, setSyncStatus] = useState(null);

  const crmTypes = [
    { id: 'salesforce', name: 'Salesforce', icon: CloudIcon, color: 'blue' },
    { id: 'hubspot', name: 'HubSpot', icon: CloudIcon, color: 'orange' },
    { id: 'zoho', name: 'Zoho CRM', icon: CloudIcon, color: 'green' },
    { id: 'dynamics365', name: 'Dynamics 365', icon: CloudIcon, color: 'purple' },
    { id: 'genesys_cloud', name: 'Genesys Cloud', icon: PhoneIcon, color: 'red' },
    { id: 'sprinklr', name: 'Sprinklr', icon: ChatBubbleLeftRightIcon, color: 'pink' },
    { id: 'custom', name: 'Custom CRM', icon: Cog6ToothIcon, color: 'gray' }
  ];

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [configRes, statusRes, syncRes] = await Promise.all([
        crmAPI.getConfig(),
        crmAPI.getStatus(),
        crmAPI.getSyncStatus()
      ]);
      setConfig(configRes.data?.config || {});
      setStatus(statusRes.data || {});
      setSyncStatus(syncRes.data || {});
    } catch (error) {
      console.error('Error loading CRM data:', error);
      toast.error('Failed to load CRM configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      console.log('Saving CRM config:', config);
      const response = await crmAPI.updateConfig(config);
      console.log('Save response:', response);
      toast.success('CRM configuration saved');
      loadData(); // Reload to get updated status
    } catch (error) {
      console.error('Error saving config:', error);
      console.error('Error details:', error.response?.data);
      toast.error(error.response?.data?.detail || 'Failed to save configuration');
    } finally {
      setSaving(false);
    }
  };

  const handleCRMConfigChange = (crmType, field, value) => {
    setConfig(prev => ({
      ...prev,
      [crmType]: {
        ...prev[crmType],
        [field]: value
      }
    }));
  };

  const handleSetActiveCRM = async (crmType) => {
    try {
      setConfig(prev => ({ ...prev, active_crm: crmType }));
      await crmAPI.updateConfig({ active_crm: crmType });
      toast.success(`${crmType} set as active CRM`);
      loadData();
    } catch (error) {
      toast.error('Failed to set active CRM');
    }
  };

  const handleSync = async () => {
    try {
      await crmAPI.syncData({
        data_types: ['customers', 'products', 'orders', 'tickets']
      });
      toast.success('CRM sync started');
      // Poll for status
      const interval = setInterval(async () => {
        const res = await crmAPI.getSyncStatus();
        setSyncStatus(res.data);
        if (!res.data?.running) {
          clearInterval(interval);
          toast.success('CRM sync completed');
        }
      }, 2000);
    } catch (error) {
      toast.error('Failed to start sync');
    }
  };

  const renderSalesforceConfig = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Instance URL</label>
          <input
            type="text"
            value={config?.salesforce?.instance_url || ''}
            onChange={(e) => handleCRMConfigChange('salesforce', 'instance_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="https://your-instance.salesforce.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Version</label>
          <input
            type="text"
            value={config?.salesforce?.api_version || 'v59.0'}
            onChange={(e) => handleCRMConfigChange('salesforce', 'api_version', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
          <input
            type="text"
            value={config?.salesforce?.client_id || ''}
            onChange={(e) => handleCRMConfigChange('salesforce', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="Consumer Key from Connected App"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
          <input
            type="password"
            value={config?.salesforce?.client_secret || ''}
            onChange={(e) => handleCRMConfigChange('salesforce', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="Consumer Secret"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Username</label>
          <input
            type="text"
            value={config?.salesforce?.username || ''}
            onChange={(e) => handleCRMConfigChange('salesforce', 'username', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="user@company.com"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Password</label>
          <input
            type="password"
            value={config?.salesforce?.password || ''}
            onChange={(e) => handleCRMConfigChange('salesforce', 'password', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Security Token</label>
        <input
          type="password"
          value={config?.salesforce?.security_token || ''}
          onChange={(e) => handleCRMConfigChange('salesforce', 'security_token', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="Security token from Salesforce"
        />
      </div>
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={config?.salesforce?.sandbox || false}
          onChange={(e) => handleCRMConfigChange('salesforce', 'sandbox', e.target.checked)}
          className="h-4 w-4 text-blue-600 border-gray-300 rounded"
        />
        <label className="ml-2 text-sm text-gray-700">Sandbox Environment</label>
      </div>
    </div>
  );

  const renderHubSpotConfig = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
        <input
          type="password"
          value={config?.hubspot?.access_token || ''}
          onChange={(e) => handleCRMConfigChange('hubspot', 'access_token', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="Private app access token"
        />
        <p className="text-xs text-gray-500 mt-1">Create a private app in HubSpot to get an access token</p>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">API Key (Legacy)</label>
        <input
          type="password"
          value={config?.hubspot?.api_key || ''}
          onChange={(e) => handleCRMConfigChange('hubspot', 'api_key', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="Legacy API key (deprecated)"
        />
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Portal ID</label>
        <input
          type="text"
          value={config?.hubspot?.portal_id || ''}
          onChange={(e) => handleCRMConfigChange('hubspot', 'portal_id', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="Your HubSpot portal ID"
        />
      </div>
    </div>
  );

  const renderZohoConfig = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
          <input
            type="text"
            value={config?.zoho?.client_id || ''}
            onChange={(e) => handleCRMConfigChange('zoho', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
          <input
            type="password"
            value={config?.zoho?.client_secret || ''}
            onChange={(e) => handleCRMConfigChange('zoho', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Refresh Token</label>
        <input
          type="password"
          value={config?.zoho?.refresh_token || ''}
          onChange={(e) => handleCRMConfigChange('zoho', 'refresh_token', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
          <select
            value={config?.zoho?.region || 'us'}
            onChange={(e) => handleCRMConfigChange('zoho', 'region', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          >
            <option value="us">United States (.com)</option>
            <option value="eu">Europe (.eu)</option>
            <option value="in">India (.in)</option>
            <option value="au">Australia (.com.au)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Redirect URI</label>
          <input
            type="text"
            value={config?.zoho?.redirect_uri || ''}
            onChange={(e) => handleCRMConfigChange('zoho', 'redirect_uri', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
      </div>
    </div>
  );

  const renderDynamicsConfig = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Tenant ID</label>
          <input
            type="text"
            value={config?.dynamics365?.tenant_id || ''}
            onChange={(e) => handleCRMConfigChange('dynamics365', 'tenant_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="Azure AD Tenant ID"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Resource URL</label>
          <input
            type="text"
            value={config?.dynamics365?.resource_url || ''}
            onChange={(e) => handleCRMConfigChange('dynamics365', 'resource_url', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="https://org.crm.dynamics.com"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
          <input
            type="text"
            value={config?.dynamics365?.client_id || ''}
            onChange={(e) => handleCRMConfigChange('dynamics365', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="Application (client) ID"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
          <input
            type="password"
            value={config?.dynamics365?.client_secret || ''}
            onChange={(e) => handleCRMConfigChange('dynamics365', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
      </div>
    </div>
  );

  const renderCustomConfig = () => (
    <div className="space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Base URL</label>
        <input
          type="text"
          value={config?.custom?.base_url || ''}
          onChange={(e) => handleCRMConfigChange('custom', 'base_url', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="https://api.yourcrm.com/v1"
        />
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Auth Type</label>
          <select
            value={config?.custom?.auth_type || 'api_key'}
            onChange={(e) => handleCRMConfigChange('custom', 'auth_type', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          >
            <option value="api_key">API Key</option>
            <option value="bearer">Bearer Token</option>
            <option value="basic">Basic Auth</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key / Token</label>
          <input
            type="password"
            value={config?.custom?.api_key || ''}
            onChange={(e) => handleCRMConfigChange('custom', 'api_key', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Auth Header Name</label>
        <input
          type="text"
          value={config?.custom?.auth_header || 'Authorization'}
          onChange={(e) => handleCRMConfigChange('custom', 'auth_header', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
        />
      </div>
    </div>
  );

  const renderGenesysCloudConfig = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Region</label>
          <select
            value={config?.genesys_cloud?.region || 'mypurecloud.com'}
            onChange={(e) => handleCRMConfigChange('genesys_cloud', 'region', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          >
            <option value="mypurecloud.com">Americas (mypurecloud.com)</option>
            <option value="mypurecloud.ie">EMEA (mypurecloud.ie)</option>
            <option value="mypurecloud.de">EMEA - Germany (mypurecloud.de)</option>
            <option value="mypurecloud.com.au">APAC (mypurecloud.com.au)</option>
            <option value="mypurecloud.jp">Japan (mypurecloud.jp)</option>
            <option value="usw2.pure.cloud">US West (usw2.pure.cloud)</option>
            <option value="cac1.pure.cloud">Canada (cac1.pure.cloud)</option>
            <option value="euw2.pure.cloud">EU West (euw2.pure.cloud)</option>
            <option value="aps1.pure.cloud">South Asia (aps1.pure.cloud)</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
          <select
            value={config?.genesys_cloud?.environment || 'production'}
            onChange={(e) => handleCRMConfigChange('genesys_cloud', 'environment', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          >
            <option value="production">Production</option>
            <option value="sandbox">Sandbox</option>
          </select>
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
          <input
            type="text"
            value={config?.genesys_cloud?.client_id || ''}
            onChange={(e) => handleCRMConfigChange('genesys_cloud', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="OAuth Client ID"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
          <input
            type="password"
            value={config?.genesys_cloud?.client_secret || ''}
            onChange={(e) => handleCRMConfigChange('genesys_cloud', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="OAuth Client Secret"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Organization ID</label>
        <input
          type="text"
          value={config?.genesys_cloud?.organization_id || ''}
          onChange={(e) => handleCRMConfigChange('genesys_cloud', 'organization_id', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="Your Genesys Cloud Organization ID"
        />
      </div>
      <div className="pt-4 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {['conversations', 'users', 'queues', 'analytics', 'routing'].map((feature) => (
            <div key={feature} className="flex items-center">
              <input
                type="checkbox"
                checked={config?.genesys_cloud?.features?.[feature] || false}
                onChange={(e) => {
                  const features = { ...config?.genesys_cloud?.features, [feature]: e.target.checked };
                  handleCRMConfigChange('genesys_cloud', 'features', features);
                }}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 capitalize">{feature}</label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderSprinklrConfig = () => (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Environment</label>
          <select
            value={config?.sprinklr?.environment || 'prod2'}
            onChange={(e) => handleCRMConfigChange('sprinklr', 'environment', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          >
            <option value="prod">Production (prod)</option>
            <option value="prod2">Production 2 (prod2)</option>
            <option value="prod3">Production 3 (prod3)</option>
            <option value="sandbox">Sandbox</option>
          </select>
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Partner ID</label>
          <input
            type="text"
            value={config?.sprinklr?.partner_id || ''}
            onChange={(e) => handleCRMConfigChange('sprinklr', 'partner_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="Sprinklr Partner ID"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client ID</label>
          <input
            type="text"
            value={config?.sprinklr?.client_id || ''}
            onChange={(e) => handleCRMConfigChange('sprinklr', 'client_id', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="OAuth Client ID"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Client Secret</label>
          <input
            type="password"
            value={config?.sprinklr?.client_secret || ''}
            onChange={(e) => handleCRMConfigChange('sprinklr', 'client_secret', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="OAuth Client Secret"
          />
        </div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">API Key</label>
          <input
            type="password"
            value={config?.sprinklr?.api_key || ''}
            onChange={(e) => handleCRMConfigChange('sprinklr', 'api_key', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="Sprinklr API Key"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-1">Access Token</label>
          <input
            type="password"
            value={config?.sprinklr?.access_token || ''}
            onChange={(e) => handleCRMConfigChange('sprinklr', 'access_token', e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
            placeholder="OAuth Access Token"
          />
        </div>
      </div>
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-1">Redirect URI</label>
        <input
          type="text"
          value={config?.sprinklr?.redirect_uri || ''}
          onChange={(e) => handleCRMConfigChange('sprinklr', 'redirect_uri', e.target.value)}
          className="w-full px-3 py-2 border border-gray-300 rounded-md text-gray-900"
          placeholder="https://your-app.com/callback"
        />
      </div>
      <div className="pt-4 border-t border-gray-200">
        <label className="block text-sm font-medium text-gray-700 mb-2">Features</label>
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2">
          {['cases', 'contacts', 'conversations', 'agents', 'analytics'].map((feature) => (
            <div key={feature} className="flex items-center">
              <input
                type="checkbox"
                checked={config?.sprinklr?.features?.[feature] || false}
                onChange={(e) => {
                  const features = { ...config?.sprinklr?.features, [feature]: e.target.checked };
                  handleCRMConfigChange('sprinklr', 'features', features);
                }}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm text-gray-700 capitalize">{feature}</label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );

  const renderConfigForm = () => {
    switch (activeTab) {
      case 'salesforce': return renderSalesforceConfig();
      case 'hubspot': return renderHubSpotConfig();
      case 'zoho': return renderZohoConfig();
      case 'dynamics365': return renderDynamicsConfig();
      case 'genesys_cloud': return renderGenesysCloudConfig();
      case 'sprinklr': return renderSprinklrConfig();
      case 'custom': return renderCustomConfig();
      default: return null;
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  return (
    <div className="p-6 max-w-6xl mx-auto">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 flex items-center">
          <CloudIcon className="h-8 w-8 mr-3 text-blue-600" />
          CRM Configuration
        </h1>
        <p className="text-gray-600 mt-2">
          Configure your CRM integrations for customer data access
        </p>
      </div>

      {/* Status Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Active CRM</p>
              <p className="text-lg font-semibold text-gray-900 capitalize">
                {config?.active_crm || 'None'}
              </p>
            </div>
            <CloudIcon className="h-8 w-8 text-blue-600 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Connected</p>
              <p className="text-lg font-semibold text-green-600">
                {status?.available_crms?.length || 0}
              </p>
            </div>
            <CheckCircleIcon className="h-8 w-8 text-green-600 opacity-50" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-600">Sync Status</p>
              <p className={`text-lg font-semibold ${syncStatus?.running ? 'text-yellow-600' : 'text-gray-600'}`}>
                {syncStatus?.running ? 'Running' : 'Idle'}
              </p>
            </div>
            <ArrowPathIcon className={`h-8 w-8 text-gray-400 ${syncStatus?.running ? 'animate-spin' : ''}`} />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow p-4">
          <button
            onClick={handleSync}
            disabled={syncStatus?.running}
            className="w-full h-full flex items-center justify-center bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition"
          >
            <ArrowPathIcon className="h-5 w-5 mr-2" />
            Sync Now
          </button>
        </div>
      </div>

      {/* CRM Tabs */}
      <div className="bg-white rounded-lg shadow">
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            {crmTypes.map((crm) => (
              <button
                key={crm.id}
                onClick={() => setActiveTab(crm.id)}
                className={`flex items-center px-6 py-4 border-b-2 font-medium text-sm ${
                  activeTab === crm.id
                    ? 'border-blue-500 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
              >
                <crm.icon className="h-5 w-5 mr-2" />
                {crm.name}
                {status?.connection_status?.[crm.id] && (
                  <CheckCircleIcon className="h-4 w-4 ml-2 text-green-500" />
                )}
              </button>
            ))}
          </nav>
        </div>

        <div className="p-6">
          {/* Enable Toggle */}
          <div className="flex items-center justify-between mb-6 pb-4 border-b border-gray-200">
            <div className="flex items-center">
              <input
                type="checkbox"
                checked={config?.[activeTab]?.enabled || false}
                onChange={(e) => handleCRMConfigChange(activeTab, 'enabled', e.target.checked)}
                className="h-4 w-4 text-blue-600 border-gray-300 rounded"
              />
              <label className="ml-2 text-sm font-medium text-gray-900">
                Enable {crmTypes.find(c => c.id === activeTab)?.name}
              </label>
            </div>
            {config?.active_crm !== activeTab && config?.[activeTab]?.enabled && (
              <button
                onClick={() => handleSetActiveCRM(activeTab)}
                className="text-sm text-blue-600 hover:text-blue-800"
              >
                Set as Active CRM
              </button>
            )}
            {config?.active_crm === activeTab && (
              <span className="text-sm text-green-600 font-medium">✓ Active CRM</span>
            )}
          </div>

          {/* Config Form */}
          {renderConfigForm()}

          {/* Save Button */}
          <div className="mt-6 pt-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:bg-gray-400 transition flex items-center"
            >
              {saving && <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />}
              Save Configuration
            </button>
          </div>
        </div>
      </div>

      {/* Data Preview */}
      <div className="mt-8 bg-white rounded-lg shadow p-6">
        <h2 className="text-xl font-bold text-gray-900 mb-4">Available Data Endpoints</h2>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <UserGroupIcon className="h-6 w-6 text-blue-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Customers</p>
              <p className="text-xs text-gray-500">/crm/customers</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <CubeIcon className="h-6 w-6 text-green-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Products</p>
              <p className="text-xs text-gray-500">/crm/products</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <ShoppingCartIcon className="h-6 w-6 text-purple-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Orders</p>
              <p className="text-xs text-gray-500">/crm/orders</p>
            </div>
          </div>
          <div className="flex items-center p-3 bg-gray-50 rounded-lg">
            <TicketIcon className="h-6 w-6 text-orange-600 mr-3" />
            <div>
              <p className="font-medium text-gray-900">Tickets</p>
              <p className="text-xs text-gray-500">/crm/tickets</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CRMConfigPage;
